import { Select, SelectProps } from '@dixa/lib-shared-habla';
import { ReadSearchApiArg, SearchUser } from 'lib-search-types';
import { getCountryCodeFromPhoneNumber } from 'lib-shared-phone-numbers';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useMemo } from 'react';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { PhoneNumberCountryFlag } from 'components/phoneNumberCountryFlag';

import { PhoneNumberUserSelectItem } from './phoneNumberUserSelect.types';
import { searchUserToPhoneNumberSelectItem } from './utils/userToSelectItem';

export type PhoneNumberUserSelectValue = {
  phoneNumber?: string;
  searchUserHit?: SearchUser;
};

export type PhoneNumberUserSelectProps = {
  phoneNumber?: string;
  fixedCountryCode?: string;
  infiniteScroll?: boolean;
  showRegionFlagPrefix?: boolean;
  onSelect: (value: PhoneNumberUserSelectValue) => void;
} & Pick<SelectProps<PhoneNumberUserSelectItem>, 'onToggle' | 'status'> &
  PropsWithClassName;

/** Represents the component for selecting a phone number. */
export const PhoneNumberUserSelect = ({
  className,
  phoneNumber,
  infiniteScroll = false,
  showRegionFlagPrefix,
  onSelect,
  onToggle,
  status,
}: PhoneNumberUserSelectProps): JSX.Element => {
  const [
    readSearchUsers,
    {
      results: users,
      originalArgs,
      isFetching,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
    },
  ] = useLazyReadSearchInfiniteQuery<'users'>({
    infinite: infiniteScroll,
  });

  const searchArg = useMemo(
    (): ReadSearchApiArg<'users'> => ({
      scope: 'users',
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'phone_number',
            operator: 'is_not_empty',
            values: [],
          },
        ],
      },
      sortBy: {
        field: 'display_name',
        order: 'asc',
      },
      pageSize: 50,
    }),
    [],
  );

  /** Handles selecting a phone number. */
  const handleSelect = useCallback(
    (item: PhoneNumberUserSelectItem) => {
      const searchUserHit = users.find(
        ({ phone_number }) => phone_number === item.id,
      );

      onSelect({
        searchUserHit,
        phoneNumber: searchUserHit?.phone_number ?? item.name,
      });
    },
    [onSelect, users],
  );

  /** Handles toggling the dropdown of the select component. */
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        readSearchUsers(searchArg);
      }
      onToggle?.(isOpen);
    },
    [onToggle, readSearchUsers, searchArg],
  );

  const handleSearch = useCallback(
    (query: string) => {
      readSearchUsers({ ...searchArg, query }, { debounceTime: 300 });
    },
    [readSearchUsers, searchArg],
  );

  /** Handles navigating to the next page. */
  const handleNextPage = useCallback((): Promise<void> => {
    return Promise.resolve(fetchNextPage());
  }, [fetchNextPage]);

  /** Handles navigating to the previous page. */
  const handlePreviousPage = useCallback((): Promise<void> => {
    return Promise.resolve(fetchPreviousPage());
  }, [fetchPreviousPage]);

  return (
    <Select
      status={status}
      prefix={
        showRegionFlagPrefix
          ? {
              type: 'custom',
              value: (
                <PhoneNumberCountryFlag
                  countryCode={getCountryCodeFromPhoneNumber(phoneNumber)}
                />
              ),
              noWrap: false,
            }
          : undefined
      }
      className={className}
      emptyMessage={
        originalArgs?.query
          ? 'No phone numbers found'
          : 'No phone numbers available'
      }
      items={
        // the search term is added as a select item,
        // but it empty search is not an option.
        originalArgs?.query && originalArgs.query != ''
          ? [
              {
                id: originalArgs.query,
                name: originalArgs.query,
              },

              ...users.map(searchUserToPhoneNumberSelectItem),
            ]
          : [...users.map(searchUserToPhoneNumberSelectItem)]
      }
      loading={isFetching}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onToggle={handleToggle}
      pagination={{
        infinite: infiniteScroll,
        nextEnable: hasNextPage,
        previousEnable: hasPreviousPage,
        onNext: handleNextPage,
        onPrevious: handlePreviousPage,
      }}
      placeholder="Type a phone number or user name"
      searchable
      selectedItems={phoneNumber ? [phoneNumber] : []}
    />
  );
};
